import {css, StyleSheet} from 'aphrodite';
import React, {RefObject, useRef} from 'react';

import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {
  DEFAULT_SOURCE_ERRORS_BY_SOURCE_DILUTION_ID,
  GenericPlateMapDisplayParams,
  SourceErrorsBySourceDilutionId,
  WellOpacityMode,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapLegend from 'samples/plate-maps/PlateMapLegend';
import PlateMapView from 'samples/plate-maps/PlateMapView';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';

export interface PlateMapPaneProps {
  /**
   * Element to render above the plate map
   */
  header: JSX.Element | null;
  plateMapDisplayParams: GenericPlateMapDisplayParams;
  isPlateMapViewOnly: boolean;
  selectedWellIndices: Set<number>;
  setSelectedWellIndices: (indices: Set<number>) => void;
  /**
   * Wells to show with preview styling.
   */
  previewedWellIndices: Set<number>;
  /**
   * List of refs which should deselect selected plate map wells on click.
   */
  deselectWellsOnClickRefs: Array<RefObject<HTMLElement>>;
  /**
   * Ref to the modal that this pane is contained in (if it is in a modal).
   */
  modalRef?: RefObject<HTMLDivElement>;
  /**
   * Maximum height of the plate map view.
   */
  maxPlateViewHeight?: string;
  /**
   * Whether to show final concentrations and quantities in well popovers.
   * During a transfer flow, we want to display final/target values, but on the inventory plate page for an
   * existing plate, we want to display the current values.
   */
  showFinalMeasurements: boolean;
  /** Type of the plate map; used to render slightly different errors/warnings for designs. */
  plateMapType: PlateMapType;
  /** Error messages for previewed sources to render in the reagent settings pane. */
  previewedSourceErrorsBySourceDilutionId?: SourceErrorsBySourceDilutionId;
  /** Whether to derive well opacity from finalConcentration or transferQuantity values in the reagent display layer. */
  applyOpacity?: WellOpacityMode;
}

/**
 * Pane that shows a plate map visualization and legend with a header.
 *
 * This wrapper keeps the header and legend aligned to the ideal size of the plate map.
 */
export default function PlateMapPane({
  header,
  plateMapDisplayParams,
  isPlateMapViewOnly,
  selectedWellIndices,
  setSelectedWellIndices,
  previewedWellIndices,
  modalRef,
  deselectWellsOnClickRefs,
  maxPlateViewHeight,
  showFinalMeasurements,
  plateMapType,
  previewedSourceErrorsBySourceDilutionId = DEFAULT_SOURCE_ERRORS_BY_SOURCE_DILUTION_ID,
  applyOpacity = WellOpacityMode.NONE,
}: PlateMapPaneProps): JSX.Element {
  const {ref: plateMapSVGRef, width} = useResizeObserver();
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className={css(styles.platePane)} ref={containerRef}>
      {header != null && (
        <div className={css(styles.header)} style={{width: `${width}px`}}>
          {header}
        </div>
      )}
      <PlateMapView
        {...plateMapDisplayParams}
        svgRef={plateMapSVGRef}
        selectedWellIndices={selectedWellIndices}
        setSelectedWellIndices={setSelectedWellIndices}
        isViewOnly={isPlateMapViewOnly}
        previewedWellIndices={previewedWellIndices}
        containingModalRef={modalRef}
        deselectWellsOnClickRefs={[...deselectWellsOnClickRefs, containerRef]}
        maxHeight={maxPlateViewHeight}
        showFinalMeasurements={showFinalMeasurements}
        plateMapType={plateMapType}
        previewedSourceErrorsBySourceDilutionId={previewedSourceErrorsBySourceDilutionId}
        applyOpacity={applyOpacity}
      />
      <div style={{width: `${width}px`, maxWidth: '100%'}}>
        <PlateMapLegend {...plateMapDisplayParams} />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  platePane: {
    minWidth: 'fit-content',
    margin: '0 auto',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header: {
    marginBottom: 8,
  },
});
